/**
 * Покупки во вкладках Level / Stars / Tech (`PurchaseRequest` из contracts): проверка и применение.
 * Сервер (S3) вызывает {@link applyPurchase} и отвечает кодом отказа как `ApiError`; клиент (C5) — {@link canPurchase}
 * для состояния кнопок. Вход не мутируется.
 */
import type { ApiErrorCode, BalanceData, PlayerState, PurchaseRequest } from "@newfungame/contracts";
import { heroLevelCost, nextStar, techDef, techLevel, techLevelCost } from "./progression";

/** Часть состояния игрока, которую меняют покупки. */
export type EconomyState = Pick<PlayerState, "coins" | "heroes" | "tech">;

/** Причина отказа — код ошибки API. */
export type PurchaseRejection = Extract<ApiErrorCode, "not_found" | "locked" | "max_level" | "insufficient_coins">;

export type PurchaseCheck = { ok: true; cost: number } | { ok: false; code: PurchaseRejection };

export type PurchaseResult<S extends EconomyState> =
  { ok: true; spent: number; state: S } | { ok: false; code: PurchaseRejection };

/**
 * Можно ли купить и за сколько. Порядок проверок: `not_found` (нет героя/Tech) → `locked` (герой не открыт) →
 * `max_level` (уровень на максимуме / 5★) → `locked` (звезда: уровень героя ниже рубежа) → `insufficient_coins`.
 */
export function canPurchase(b: BalanceData, state: EconomyState, req: PurchaseRequest): PurchaseCheck {
  const cost = priceOf(b, state, req);
  if (!cost.ok) return cost;
  if (state.coins < cost.cost) return { ok: false, code: "insufficient_coins" };
  return cost;
}

/** Применяет покупку: новое состояние со списанными монетами и поднятым уровнем/звездой. */
export function applyPurchase<S extends EconomyState>(
  b: BalanceData,
  state: S,
  req: PurchaseRequest,
): PurchaseResult<S> {
  const check = canPurchase(b, state, req);
  if (!check.ok) return check;
  const coins = state.coins - check.cost;
  if (req.kind === "tech") {
    const t = techDef(b, req.techId)!;
    return {
      ok: true,
      spent: check.cost,
      state: { ...state, coins, tech: { ...state.tech, [t.id]: techLevel(t, state.tech) + 1 } },
    };
  }
  const heroes = state.heroes.map((p) => {
    if (p.heroId !== req.heroId) return p;
    return req.kind === "heroLevel" ? { ...p, level: p.level + 1 } : { ...p, stars: p.stars + 1 };
  });
  return { ok: true, spent: check.cost, state: { ...state, coins, heroes } };
}

function priceOf(b: BalanceData, state: EconomyState, req: PurchaseRequest): PurchaseCheck {
  if (req.kind === "tech") {
    const t = techDef(b, req.techId);
    if (!t) return { ok: false, code: "not_found" };
    const cost = techLevelCost(b, t.id, techLevel(t, state.tech));
    return cost === null ? { ok: false, code: "max_level" } : { ok: true, cost };
  }
  const p = state.heroes.find((x) => x.heroId === req.heroId);
  if (!p || !b.heroes.some((h) => h.id === req.heroId)) return { ok: false, code: "not_found" };
  if (!p.unlocked) return { ok: false, code: "locked" };
  if (req.kind === "heroLevel") {
    const cost = heroLevelCost(b, p.heroId, p.level);
    return cost === null ? { ok: false, code: "max_level" } : { ok: true, cost };
  }
  const star = nextStar(b, p.stars);
  if (!star) return { ok: false, code: "max_level" };
  if (p.level < star.requiredLevel) return { ok: false, code: "locked" };
  return { ok: true, cost: star.cost };
}
